import React, { useEffect, useState } from "react";
import { getAllReports } from "../services/api";
import Skeleton from "../Components/SkeletonLoader";
import {
  BarChart3,
  Activity,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Calendar,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import jsPDF from "jspdf";
import "jspdf-autotable";

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportType, setReportType] = useState("daily");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const setPeriodDates = (type) => {
    const today = new Date();
    const formatDate = (date) => date.toISOString().split("T")[0];

    let from = new Date();
    const to = formatDate(today);

    switch (type) {
      case "daily":
        from = today;
        break;
      case "weekly":
        from.setDate(today.getDate() - 7);
        break;
      case "monthly":
        from.setDate(today.getDate() - 30);
        break;
      case "yearly":
        from.setDate(today.getDate() - 365);
        break;
      default:
        return;
    }

    setFromDate(formatDate(from));
    setToDate(to);
    setReportType(type);
    setPageNo(1); // Reset page on period change
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await getAllReports(reportType, fromDate, toDate, pageNo, pageSize);
      setReports(response?.data || []);
      setTotalCount(response?.count || 0);
    } catch (err) {
      console.error("Failed to fetch reports:", err);
      setError("Failed to load reports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [reportType, fromDate, toDate, pageNo, pageSize]);

  const downloadPDF = () => {
    const doc = new jsPDF();

    // Add Title
    doc.setFontSize(18);
    doc.text("System Reliability Report", 14, 20);

    let periodText = reportType.charAt(0).toUpperCase() + reportType.slice(1);
    if (fromDate && toDate) {
      periodText = `Range: ${fromDate} to ${toDate}`;
    } else if (fromDate) {
      periodText = `From: ${fromDate}`;
    } else if (toDate) {
      periodText = `To: ${toDate}`;
    }

    doc.text(`Period: ${periodText} | Generated: ${new Date().toLocaleString()}`, 14, 28);

    // Define Table Columns
    const tableColumn = [
      "Device Name",
      "IP Address",
      "Uptime",
      "Avg Response",
      "Online Checks",
      "Offline Checks",
      "Total Checks",
    ];
    const tableRows = [];

    reports.forEach((report) => {
      const reportData = [
        report.deviceName,
        report.ip,
        `${report.uptimePercentage?.toFixed(2)}%`,
        `${report.averageResponseTime} ms`,
        report.onlineChecks,
        report.offlineChecks,
        report.totalChecks,
      ];
      tableRows.push(reportData);
    });

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 35,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: 255,
        fontStyle: "bold",
      }, // Blue-600
      alternateRowStyles: { fillColor: [249, 250, 251] }, // Gray-50
    });

    doc.save(
      `system_report_${reportType}_${new Date().toISOString().slice(0, 10)}.pdf`,
    );
  };

  const getUptimeColor = (percentage) => {
    if (percentage >= 99)
      return "text-emerald-700 bg-emerald-50 border border-emerald-100";
    if (percentage >= 95)
      return "text-amber-700 bg-amber-50 border border-amber-100";
    return "text-rose-700 bg-rose-50 border border-rose-100";
  };

  const getProgressBarColor = (percentage) => {
    if (percentage >= 99) return "bg-emerald-500";
    if (percentage >= 95) return "bg-amber-500";
    return "bg-rose-500";
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            System Reliability Reports
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Performance metrics, uptime analysis, and availability statistics.
          </p>
        </div>

        {/* Filter Controls */}
        <div className="flex flex-wrap items-center gap-4 bg-white p-2 rounded-xl border border-gray-200 shadow-sm">
          {/* Period Selector */}
          <div className="flex bg-gray-100 p-1 rounded-lg">
            {["daily", "weekly", "monthly", "yearly"].map((type) => (
              <button
                key={type}
                onClick={() => setPeriodDates(type)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${reportType === type && !fromDate && !toDate
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
                  }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>

          <div className="h-8 w-px bg-gray-200 mx-2 hidden md:block" />

          {/* Date Pickers */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                From
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  if (e.target.value) setReportType(null); // Clear preset if custom date is used
                }}
                className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                To
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  if (e.target.value) setReportType(null); // Clear preset if custom date is used
                }}
                className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
            </div>
            {(fromDate || toDate) && (
              <button
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                  setReportType("daily");
                }}
                className="text-xs font-medium text-rose-600 hover:text-rose-700 underline underline-offset-4"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-xl border border-red-200 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Main Content Card */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
        {/* Table Header / Toolbar */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-700">
            <BarChart3 className="h-5 w-5 text-gray-400" />
            <span className="font-semibold text-sm">Performance Data</span>
          </div>
          <button
            onClick={downloadPDF}
            disabled={loading || reports.length === 0}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-3.5 w-3.5" />
            Download PDF
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                <th className="px-6 py-4">Device Details</th>
                <th className="px-6 py-4">Uptime</th>
                <th className="px-6 py-4">Response Time</th>
                <th className="px-6 py-4">Health Checks</th>
                <th className="px-6 py-4">Reliability Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                // Loading Skeleton
                [1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    <td className="px-6 py-4">
                      <Skeleton className="h-10 w-48" />
                    </td>
                    <td className="px-6 py-4">
                      <Skeleton className="h-6 w-16" />
                    </td>
                    <td className="px-6 py-4">
                      <Skeleton className="h-6 w-24" />
                    </td>
                    <td className="px-6 py-4">
                      <Skeleton className="h-6 w-32" />
                    </td>
                    <td className="px-6 py-4">
                      <Skeleton className="h-2 w-full rounded-full" />
                    </td>
                  </tr>
                ))
              ) : reports.length > 0 ? (
                reports.map((report) => (
                  <tr
                    key={report.ip}
                    className="group hover:bg-blue-50/30 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-900 text-sm group-hover:text-blue-600 transition-colors">
                          {report.deviceName}
                        </span>
                        <span className="text-xs text-gray-500 font-mono mt-0.5">
                          {report.ip}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${getUptimeColor(report.uptimePercentage)}`}
                      >
                        {report.uptimePercentage?.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Activity className="h-4 w-4 text-gray-400" />
                        <span className="font-mono text-sm font-medium">
                          {report.averageResponseTime} ms
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3 text-sm">
                        <div
                          className="flex items-center gap-1.5"
                          title="Online Checks"
                        >
                          <CheckCircle className="h-4 w-4 text-emerald-500" />
                          <span className="font-mono font-medium text-gray-700">
                            {report.onlineChecks}
                          </span>
                        </div>
                        <span className="text-gray-300">|</span>
                        <div
                          className="flex items-center gap-1.5"
                          title="Offline Checks"
                        >
                          <XCircle className="h-4 w-4 text-rose-500" />
                          <span className="font-mono font-medium text-gray-700">
                            {report.offlineChecks}
                          </span>
                        </div>
                        <span className="text-gray-300">|</span>
                        <div className="text-xs text-gray-400">
                          Total: {report.totalChecks}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-middle">
                      <div className="w-full max-w-[140px]">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-500 font-medium">
                            Score
                          </span>
                          <span className="text-gray-900 font-bold">
                            {report.uptimePercentage?.toFixed(1)}%
                          </span>
                        </div>
                        <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${getProgressBarColor(report.uptimePercentage)}`}
                            style={{ width: `${report.uptimePercentage}%` }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="p-3 bg-gray-50 rounded-full">
                        <Calendar className="h-6 w-6 text-gray-300" />
                      </div>
                      <span className="text-sm font-medium">
                        No report data available for this period.
                      </span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {totalCount > 0 && (
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
            <span className="text-xs text-gray-500 font-medium">
              Showing <span className="text-gray-900 font-bold">{reports.length}</span> of{" "}
              <span className="text-gray-900 font-bold">{totalCount}</span>
            </span>

            <div className="flex items-center gap-3">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPageNo(1);
                }}
                className="text-xs border-gray-300 rounded-lg py-1.5 pl-2 pr-6 bg-white focus:ring-blue-500 focus:border-blue-500 outline-none shadow-sm"
              >
                {[10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPageNo((prev) => Math.max(prev - 1, 1))}
                  disabled={pageNo === 1}
                  className="p-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-3 text-xs font-medium text-gray-700">
                  Page {pageNo} of {Math.ceil(totalCount / pageSize) || 1}
                </span>
                <button
                  onClick={() =>
                    setPageNo((prev) =>
                      Math.min(prev + 1, Math.ceil(totalCount / pageSize)),
                    )
                  }
                  disabled={pageNo >= Math.ceil(totalCount / pageSize)}
                  className="p-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
